import { currency, toFiniteNumber } from '@/shared/lib/format';
import { passbookCurrency } from '@/shared/lib/passbook-utils';
import {
  computeMovementValidUntil,
} from '@/features/leads/model/movement-validity';
import type { MovementProgram } from '@/features/leads/model/movement-validity';
import type { Passbook, PointLog, PointLogAction, PointLogProduct } from '@/shared/types';

export type { MovementProgram } from '@/features/leads/model/movement-validity';

export function movementProgram(passbook: Passbook): MovementProgram {
  if (passbook.hasTransaction || passbook.hasGiftcard) return 'balance';
  if (passbook.hasCashback) return 'cashback';
  if (passbook.hasPoints) return 'points';
  return 'stamps';
}

/** Usa passbook + texto do log (como o dash legado exibe `transactionHistory`). */
function resolveProgram(passbook: Passbook, log: Pick<PointLog, 'transactionHistory'>): MovementProgram {
  const base = movementProgram(passbook);
  if (base === 'cashback' || base === 'balance') return base;

  const h = log.transactionHistory?.toLowerCase() ?? '';
  if (/\breais\b|cashback/.test(h)) return 'cashback';
  if (/\bselos?\b/.test(h)) return 'stamps';
  if (/\bpontos?\b/.test(h)) return 'points';
  return base;
}

const parseLegacyNumber = toFiniteNumber;

/** Ignora ruído de ponto flutuante (`1.77e-15`). */
function effectiveDelta(value: unknown): number {
  const n = parseLegacyNumber(value);
  return Math.abs(n) < 0.001 ? 0 : n;
}

function readLogField(raw: Record<string, unknown>, slug: string): string | undefined {
  const fields = raw.fields;
  if (!Array.isArray(fields)) return undefined;
  for (const field of fields) {
    const f = field as { slug?: string; value?: unknown };
    if (f.slug === slug && f.value != null && String(f.value).trim() !== '') {
      return String(f.value);
    }
  }
  return undefined;
}

/** Infere ação a partir do texto legado (`transactionHistory`), como o dash exibe. */
function inferActionFromTransactionHistory(history?: string): PointLogAction | undefined {
  if (!history?.trim()) return undefined;
  const text = history
    .trim()
    .split('\n')[0]
    ?.replace(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2}\s*-\s*/i, '')
    .toLowerCase() ?? '';

  if (/expirad/.test(text)) return 'expirePoints';
  if (/resgate/.test(text)) return 'resgate';
  if (/cancelad/.test(text)) return 'cancel';
  if (/adicionado|creditado/.test(text)) return 'addPoints';
  if (/removid/.test(text)) return 'resgate';
  return undefined;
}

function normalizeAction(
  raw: string | undefined,
  redemption?: boolean,
  transactionHistory?: string,
): PointLogAction {
  if (raw === 'cancel') return 'cancel';
  if (raw === 'expirePoints') return 'expirePoints';
  if (raw === 'remove' || raw === 'removePoints' || (raw === 'input' && redemption)) return 'resgate';
  if (raw === 'addPoints') return 'addPoints';
  if (raw === 'input') return 'input';
  if (raw === 'payment' || raw === 'debit' || raw === 'credit') return 'addPoints';

  const fromHistory = inferActionFromTransactionHistory(transactionHistory);
  if (fromHistory) return fromHistory;

  return 'addPoints';
}

function resolveMovementAction(log: Pick<PointLog, 'action' | 'value' | 'transactionHistory'>): PointLogAction {
  const normalized = log.action;
  if (normalized === 'resgate' || normalized === 'expirePoints' || normalized === 'cancel') {
    return normalized;
  }

  const fromHistory = inferActionFromTransactionHistory(log.transactionHistory);
  if (fromHistory) {
    if (normalized === 'addPoints' && (fromHistory === 'resgate' || fromHistory === 'expirePoints')) {
      return fromHistory;
    }
    if (!normalized) return fromHistory;
  }

  if (normalized) return normalized;
  return log.value >= 0 ? 'addPoints' : 'resgate';
}

function resolveValidUntilFromApi(raw: Record<string, unknown>): string | undefined {
  const exp = raw.expirationDate;
  if (exp == null || exp === '') return undefined;
  const date = new Date(exp as string | number);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function isRegistrationMovement(action: PointLogAction, delta: number): boolean {
  return action === 'input' && delta === 0;
}

/** Título da movimentação (cashback / pontos / selos / saldo). */
export function movementActionLabel(log: PointLog, passbook: Passbook): string {
  const program = resolveProgram(passbook, log);
  const action = resolveMovementAction(log);
  const delta = Math.abs(log.value);

  if (isRegistrationMovement(action, delta)) return 'Cadastro';

  const stampInput = action === 'input' && delta > 0;

  const labels: Record<MovementProgram, Record<string, string>> = {
    cashback: {
      addPoints: 'Cashback adicionado',
      input: 'Cashback adicionado',
      resgate: 'Resgate de cashback',
      cancel: 'Operação cancelada',
      expirePoints: 'Cashback expirado',
    },
    points: {
      addPoints: 'Pontos adicionados',
      input: 'Pontos adicionados',
      resgate: 'Resgate de pontos',
      cancel: 'Operação cancelada',
      expirePoints: 'Pontos expirados',
    },
    stamps: {
      addPoints: 'Selos adicionados',
      input: stampInput ? 'Selos adicionados' : 'Cadastro',
      resgate: 'Resgate de selos',
      cancel: 'Operação cancelada',
      expirePoints: 'Selos expirados',
    },
    balance: {
      addPoints: 'Crédito adicionado',
      input: 'Cadastro',
      resgate: 'Débito',
      cancel: 'Operação cancelada',
      expirePoints: 'Saldo expirado',
    },
  };

  return labels[program][action] ?? 'Movimentação';
}

function formatUnitQty(abs: number, program: MovementProgram, passbook: Passbook): string {
  if (program === 'cashback' || program === 'balance') {
    return currency(abs, passbookCurrency(passbook));
  }
  if (program === 'points') {
    const qty = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(abs);
    return `${qty} ${abs === 1 ? 'ponto' : 'pontos'}`;
  }
  const n = Math.round(abs);
  return `${n} ${n === 1 ? 'selo' : 'selos'}`;
}

/** Variação do saldo (pointsChanged) — nunca o saldo acumulado em `value` da API. */
export function formatMovementDelta(log: PointLog, passbook: Passbook): string {
  const abs = Math.abs(log.value);
  const sign = log.value >= 0 ? '+' : '−';
  const program = resolveProgram(passbook, log);
  return `${sign}${formatUnitQty(abs, program, passbook)}`;
}

/** Data de validade do saldo gerado na movimentação. */
export function resolveLogValidUntil(log: PointLog, passbook?: Passbook): string | undefined {
  if (!passbook) {
    return log.validUntil;
  }
  const until = computeMovementValidUntil(log, passbook, resolveProgram(passbook, log));
  return until?.toISOString();
}

export { formatMovementValidUntilCaption, formatMovementValidUntilShort } from '@/features/leads/model/movement-validity';

function mapProducts(details: unknown): PointLogProduct[] | undefined {
  if (!Array.isArray(details) || details.length === 0) return undefined;
  return details.map((item) => {
    const d = item as Record<string, unknown>;
    return {
      sku: d.sku as string | undefined,
      name: (d.name ?? d.nome) as string | undefined,
      count: parseLegacyNumber(d.count ?? d.qtde ?? d.qty) || 1,
      price: parseLegacyNumber(d.price) || undefined,
      vendedor: (d.vendedor ?? d.seller) as string | undefined,
    };
  });
}

function resolveProdutoResgate(raw: Record<string, unknown>): string | undefined {
  const candidates = [raw.produto_resgate, raw._produto_resgate];
  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') return String(candidate);
  }
  const produto = raw.produto as Array<{ name?: string }> | undefined;
  if (produto?.[0]?.name) return produto[0].name;
  return undefined;
}

/**
 * Valor da compra (R$) — sempre `moneyValorCompra` nos fields.
 * `amount` na API é quantidade de selos/cashback, não valor da compra.
 */
function resolvePurchasePrice(raw: Record<string, unknown>): number | undefined {
  const money = readLogField(raw, 'moneyValorCompra');
  if (money) {
    const price = parseLegacyNumber(money);
    if (price > 0) return price;
  }

  // Logs de resgate carregam o saldo resultante em `amount`/`value` (ex.:
  // POST /resgate), nunca um preço de compra — nunca usar o fallback de
  // amount nesses casos, senão o saldo vaza como se fosse R$.
  if (raw.redemption) return undefined;

  const action = raw.action as string | undefined;
  if (action === 'payment' || action === 'debit' || action === 'credit') {
    const amount = parseLegacyNumber(raw.amount);
    return amount > 0 ? amount : undefined;
  }

  return undefined;
}

/**
 * Delta da movimentação — espelha `pointsChanged` da API.
 * Não usar `value` da API (saldo de selos no cartão).
 * Para selos sem `pointsChanged`, o dash legado embute o delta em `transactionHistory`.
 */
function isDebitAction(rawAction: string | undefined, action: PointLogAction, redemption?: boolean): boolean {
  return (
    rawAction === 'removePoints' ||
    rawAction === 'remove' ||
    action === 'resgate' ||
    Boolean(redemption)
  );
}

/** Extrai delta numérico do texto legado (padrão do dash para selos). */
export function parseDeltaFromTransactionHistory(
  history: string | undefined,
  action: PointLogAction,
): number | undefined {
  if (!history?.trim()) return undefined;
  const line = (history.split('\n').find((l) => l.trim()) ?? history)
    .replace(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2}\s*[-|]\s*/i, '')
    .trim();

  // Operador: "3 selos efetuada por João"
  let m = line.match(/^(\d+(?:[.,]\d+)?)\s+selos?\s+efetuad/i);
  if (m) {
    const n = parseLegacyNumber(m[1]);
    return n !== 0 ? n : undefined;
  }

  // Integração / resgate: "Adicionado 2 selos" | "Delivery 3 selos" | "Resgate de 10 selos"
  m = line.match(/(?:Adicionado|Delivery|Resgate de)\s+([\d.,]+)/i);
  if (m) {
    const n = parseLegacyNumber(m[1]);
    if (n === 0) return undefined;
    if (/resgate/i.test(line) || action === 'resgate') return -Math.abs(n);
    return Math.abs(n);
  }

  // Genérico: "… N selo(s) …"
  m = line.match(/([\d.,]+)\s+selos?\b/i);
  if (m) {
    const n = parseLegacyNumber(m[1]);
    if (n === 0) return undefined;
    if (/resgate|removid|expirad/i.test(line) || action === 'resgate' || action === 'expirePoints') {
      return -Math.abs(n);
    }
    return Math.abs(n);
  }

  return undefined;
}

function inferStampsProgram(raw: Record<string, unknown>): boolean {
  const h = String(raw.transactionHistory ?? '').toLowerCase();
  return /\bselos?\b/.test(h);
}

export type NormalizePointLogOptions = {
  /** Programa do passbook (selos sem "selos" no histórico — ex.: POST /resgate). */
  program?: MovementProgram;
  /** Tamanho da cartela — delta negativo no resgate sem pointsChanged. */
  maxInputValidation?: number;
};

/** Pares de saldo (log anterior → este log) pra derivar o delta real de um resgate. */
export type BalanceHint = {
  prevBalance?: number;
  thisBalance?: number;
  /** Houve algum saldo conhecido antes deste log (mesmo que não sirva pro diff). */
  hadPriorBalance?: boolean;
};

/**
 * Diferença real de saldo entre o log anterior e este — mais confiável que
 * assumir o tamanho ATUAL da cartela (`maxInputValidation`), que pode ter
 * mudado desde que o resgate aconteceu (ex.: loja trocou cartela de 10 pra
 * 20 selos; resgates antigos de 10 não podem usar o valor de hoje).
 */
function balanceDiffDelta(hint?: BalanceHint): number | undefined {
  if (!hint) return undefined;
  const { prevBalance, thisBalance } = hint;
  if (prevBalance == null || thisBalance == null) return undefined;
  if (!Number.isFinite(prevBalance) || !Number.isFinite(thisBalance)) return undefined;
  const diff = prevBalance - thisBalance;
  return diff > 0 ? -diff : undefined;
}

function resolveLogDelta(
  raw: Record<string, unknown>,
  action: PointLogAction,
  opts?: NormalizePointLogOptions,
  balanceHint?: BalanceHint,
): number {
  const rawAction = raw.action as string | undefined;
  const redemption = Boolean(raw.redemption);
  const pointsChanged = effectiveDelta(raw.pointsChanged);
  const isStamps = inferStampsProgram(raw) || opts?.program === 'stamps';
  const cartela =
    opts?.maxInputValidation != null && opts.maxInputValidation > 0
      ? Math.abs(opts.maxInputValidation)
      : 0;

  if (rawAction === 'expirePoints' || action === 'expirePoints') {
    if (pointsChanged > 0) return -pointsChanged;
    if (pointsChanged < 0) return pointsChanged;
    if (isStamps) {
      const fromHistory = parseDeltaFromTransactionHistory(
        raw.transactionHistory as string | undefined,
        action,
      );
      if (fromHistory != null) return fromHistory < 0 ? fromHistory : -Math.abs(fromHistory);
    }
    return 0;
  }

  if (isDebitAction(rawAction, action, redemption)) {
    if (pointsChanged > 0) return -pointsChanged;
    if (pointsChanged < 0) return pointsChanged;
    if (isStamps) {
      const fromHistory = parseDeltaFromTransactionHistory(
        raw.transactionHistory as string | undefined,
        'resgate',
      );
      if (fromHistory != null) return fromHistory < 0 ? fromHistory : -Math.abs(fromHistory);
    }
    // POST /resgate: action:input, redemption:true, sem pointsChanged nem
    // texto reconhecível. A diferença de saldo entre logs é agnóstica de
    // programa — vale tanto pra selos quanto pra cashback/pontos.
    const diff = balanceDiffDelta(balanceHint);
    if (diff != null) return diff;
    // Cartela só sem nenhum saldo anterior (primeiro log / histórico cego).
    // Se já vimos saldo antes mas o diff falhou (buraco no /log), preferir 0
    // a inventar o tamanho ATUAL da cartela (ex.: "-20" quando o resgate foi 10).
    if (
      redemption &&
      cartela > 0 &&
      (opts?.program === 'stamps' || isStamps) &&
      !balanceHint?.hadPriorBalance
    ) {
      return -cartela;
    }
    return 0;
  }

  if (rawAction === 'cancel' || action === 'cancel') {
    return pointsChanged;
  }

  if (pointsChanged !== 0) return pointsChanged;

  // Selos: preferir transactionHistory; depois amount (só quando não há
  // pointsChanged nem transactionHistory parseável — nunca no resgate, que
  // tem sua própria cadeia acima, já que amount ali é o saldo restante).
  if (isStamps) {
    const fromHistory = parseDeltaFromTransactionHistory(
      raw.transactionHistory as string | undefined,
      action,
    );
    if (fromHistory != null && fromHistory !== 0) return fromHistory;
    const amount = effectiveDelta(raw.amount);
    if (amount !== 0) return Math.abs(amount);
    return 0;
  }

  if (action === 'input') {
    const amount = effectiveDelta(raw.amount);
    if (amount > 0) return amount;
    return 0;
  }

  if (action === 'addPoints') {
    const amount = effectiveDelta(raw.amount);
    if (amount > 0) return amount;
    return 0;
  }

  return 0;
}

function resolveExternalId(raw: Record<string, unknown>): string | undefined {
  const fromField = readLogField(raw, 'numberCodCupom');
  if (fromField) return fromField;
  const ext = raw.externalId;
  return ext != null && String(ext).trim() !== '' ? String(ext) : undefined;
}

/** Opções a partir do passbook (cartela / programa). */
export function normalizeOptsFromPassbook(passbook?: Passbook | null): NormalizePointLogOptions | undefined {
  if (!passbook) return undefined;
  return {
    program: movementProgram(passbook),
    maxInputValidation: passbook.maxInputValidation,
  };
}

/**
 * Integrações às vezes gravam a ação (`add_points`) em `source` no lugar da loja.
 * Sem este filtro o histórico mostra "add_points · Compra de R$ …".
 */
const ACTION_SOURCE =
  /^(add_?points|remove_?points|add|added|credit|debit|input|output|remove|removed|cancel|signup|expired?_?points|bonus|payment)$/i;

function storeLabel(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  if (!value || ACTION_SOURCE.test(value)) return undefined;
  return value;
}

/** Converte log da API legada (`/log/:serial`) para o formato usado na UI. */
export function normalizePointLog(
  raw: unknown,
  opts?: NormalizePointLogOptions,
  balanceHint?: BalanceHint,
): PointLog {
  const entry = (raw ?? {}) as Record<string, unknown>;
  const updated = entry.updated ?? entry.date;
  const date =
    updated != null && updated !== ''
      ? new Date(updated as string | number).toISOString()
      : '';

  const transactionHistory =
    entry.transactionHistory != null && String(entry.transactionHistory).trim() !== ''
      ? String(entry.transactionHistory).trim()
      : undefined;

  const action = normalizeAction(
    entry.action as string | undefined,
    Boolean(entry.redemption),
    transactionHistory,
  );

  const pointsChanged = effectiveDelta(entry.pointsChanged);
  const balanceAfter = parseLegacyNumber(entry.points);
  const expirePointsRaw = entry.expire_points;
  const expirePointsParsed =
    expirePointsRaw != null && expirePointsRaw !== ''
      ? effectiveDelta(expirePointsRaw)
      : undefined;

  return {
    _id: entry._id != null ? String(entry._id) : undefined,
    value: resolveLogDelta(entry, action, opts, balanceHint),
    pointsChanged: pointsChanged !== 0 ? pointsChanged : undefined,
    balanceAfter: balanceAfter > 0 || entry.points != null ? balanceAfter : undefined,
    date,
    action,
    loja: storeLabel(entry.source) ?? storeLabel(entry.storeName) ?? storeLabel(entry.loja),
    price: resolvePurchasePrice(entry),
    externalId: resolveExternalId(entry),
    produtoResgate: resolveProdutoResgate(entry),
    products: mapProducts(entry.details ?? entry.products),
    validUntil: resolveValidUntilFromApi(entry),
    expirePoints: expirePointsParsed != null && expirePointsParsed > 0.001 ? expirePointsParsed : undefined,
    transactionHistory,
  };
}

/** Extrai saldo de textos legados: "… - Saldo: 20". */
function parseSaldoFromHistory(history: unknown): number | undefined {
  if (history == null || history === '') return undefined;
  const m = String(history).match(/Saldo:\s*([\d.,]+)/i);
  if (!m) return undefined;
  const n = parseLegacyNumber(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Saldo que este log deixou no cartão.
 * Ordem: value → currentBalance → points → "Saldo: N" no transactionHistory.
 * (GET /log omite selos intermediários de lote; precisamos de várias fontes.)
 */
function rawResultingBalance(entry: Record<string, unknown>): number | undefined {
  for (const key of ['value', 'currentBalance', 'points'] as const) {
    const raw = entry[key];
    if (raw == null || raw === '') continue;
    const n = parseLegacyNumber(raw);
    if (Number.isFinite(n)) return n;
  }
  return parseSaldoFromHistory(entry.transactionHistory);
}

/** Último saldo conhecido estritamente maior que `thisBalance` (pula 0→0 / empates). */
function lastHigherBalance(known: number[], thisBalance: number | undefined): number | undefined {
  if (thisBalance == null) {
    return known.length > 0 ? known[known.length - 1] : undefined;
  }
  for (let i = known.length - 1; i >= 0; i--) {
    const bal = known[i];
    if (bal != null && bal > thisBalance) return bal;
  }
  return undefined;
}

export function normalizePointLogs(raw: unknown, opts?: NormalizePointLogOptions): PointLog[] {
  const entries = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
  if (entries.length === 0) return [];

  // Ordena cronologicamente (mais antigo primeiro) pra comparar saldos.
  const chronological = entries
    .map((row) => {
      const entry = (row ?? {}) as Record<string, unknown>;
      const updated = entry.updated ?? entry.date;
      const time =
        updated != null && updated !== '' ? new Date(updated as string | number).getTime() : NaN;
      return { entry, time: Number.isFinite(time) ? (time as number) : 0 };
    })
    .sort((a, b) => a.time - b.time);

  const knownBalances: number[] = [];
  const logs = chronological.map(({ entry }) => {
    const thisBalance = rawResultingBalance(entry);
    const prevBalance = lastHigherBalance(knownBalances, thisBalance);
    const log = normalizePointLog(entry, opts, {
      prevBalance,
      thisBalance,
      hadPriorBalance: knownBalances.length > 0,
    });
    if (thisBalance != null) knownBalances.push(thisBalance);
    return log;
  });

  return logs.sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Saldo atual a partir do GET /registration/:id (antes de substituir logs pelo /log/:serial).
 * Espelha o dash legado: último log embutido em `user.logs` ou `count_inputs`.
 */
export function deriveLeadBalanceFromRegistration(
  logs: unknown,
  countInputs?: unknown,
  passbook?: Passbook,
): number | undefined {
  let lastLog: Record<string, unknown> | null = null;
  if (Array.isArray(logs) && logs.length > 0) {
    lastLog = logs[logs.length - 1] as Record<string, unknown>;
  } else if (logs && typeof logs === 'object') {
    lastLog = logs as Record<string, unknown>;
  }

  if (lastLog) {
    const program = passbook ? movementProgram(passbook) : 'points';
    if (program === 'stamps') {
      const stamps = parseLegacyNumber(lastLog.value);
      if (stamps > 0) return stamps;
    } else {
      const points = parseLegacyNumber(lastLog.points);
      if (points > 0) return points;
    }
    const value = parseLegacyNumber(lastLog.value);
    if (value !== 0) return value;
  }

  if (countInputs != null && countInputs !== '') {
    const n = parseLegacyNumber(countInputs);
    return Number.isFinite(n) ? n : undefined;
  }

  return undefined;
}
